import React, { useState } from 'react';
import {
  Card, Form, Input, Select, Button, Switch, Upload, Typography,
  Space, Divider, message, Alert, Row, Col, Tag,
} from 'antd';
import {
  BellOutlined, UploadOutlined, SendOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import { sendNotificationApi } from '../../api/admin';

const { Title, Text } = Typography;
const { TextArea } = Input;

const LANGUAGES = [
  { value: 'hi',   label: 'Hindi (hi)' },
  { value: 'ma',   label: 'Marathi (ma)' },
  { value: 'gu',   label: 'Gujarati (gu)' },
  { value: 'beng', label: 'Bengali (beng)' },
  { value: 'en',   label: 'English (en)' },
  { value: 'ta',   label: 'Tamil (ta)' },
  { value: 'te',   label: 'Telugu (te)' },
  { value: 'ur',   label: 'Urdu (ur)' },
];

const TYPES = [
  { value: 'user',    label: 'User' },
  { value: 'feature', label: 'Feature' },
  { value: 'general', label: 'General' },
  { value: 'update',  label: 'Update' },
];

export default function AdminNotifications() {
  const [form] = Form.useForm();
  const [submitting, setSubmitting]       = useState(false);
  const [outdatedUsers, setOutdatedUsers] = useState(false);
  const [imageFile, setImageFile]         = useState(null); // in-app notification list
  const [pushImageFile, setPushImageFile] = useState(null); // phone push notification
  const [result, setResult]               = useState(null);

  const onFinish = async (values) => {
    setResult(null);
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('title',    values.title);
      fd.append('message',  values.message);
      fd.append('language', values.language);
      fd.append('type',     values.type);

      if (values.link)       fd.append('link',       values.link);
      if (values.link_title) fd.append('link_title', values.link_title);
      if (imageFile)         fd.append('image',      imageFile);
      if (pushImageFile)     fd.append('push_image', pushImageFile);

      if (values.not_subscribed) fd.append('not_subscribed', 'true');
      if (values.expired_users)  fd.append('expired_users',  'true');
      if (values.outdated_users) {
        fd.append('outdated_users',     'true');
        fd.append('latest_app_version', values.latest_app_version);
      }

      const res = await sendNotificationApi(fd);
      setResult({ success: true, message: res.data.message, total: res.data.total_users });
      message.success(`Sent to ${res.data.total_users} users`);
      form.resetFields();
      setImageFile(null);
      setPushImageFile(null);
      setOutdatedUsers(false);
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.errors) {
        const firstErr = Object.values(errData.errors).flat()[0];
        message.error(firstErr || 'Validation failed');
        setResult({ success: false, message: firstErr });
      } else if (errData?.message) {
        message.error(errData.message);
        setResult({ success: false, message: errData.message });
      } else {
        message.error('Failed to send notification');
        setResult({ success: false, message: 'Failed to send notification' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Keeps the file for the form submit instead of uploading it straight away.
  const imagePicker = ({ types, typesLabel, maxMb, onPick }) => (file) => {
    if (!types.includes(file.type)) {
      message.error(`Only ${typesLabel} images are allowed`);
      return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 > maxMb) {
      message.error(`Image must be smaller than ${maxMb} MB`);
      return Upload.LIST_IGNORE;
    }
    onPick(file);
    return false;
  };

  const beforeUpload = imagePicker({
    types: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
    typesLabel: 'JPEG, PNG, or WebP',
    maxMb: 5,
    onPick: setImageFile,
  });

  // FCM drops push images over 1 MB, and WebP doesn't show on every phone.
  const beforePushUpload = imagePicker({
    types: ['image/jpeg', 'image/jpg', 'image/png'],
    typesLabel: 'JPEG or PNG',
    maxMb: 1,
    onPick: setPushImageFile,
  });

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Title level={4} style={{ margin: 0, color: '#1a6b2a' }}>
          <BellOutlined /> Send Push Notification
        </Title>
        <Text type="secondary">Send FCM push notifications to filtered users</Text>
      </div>

      {result && (
        <Alert
          type={result.success ? 'success' : 'error'}
          message={result.success ? `Sent to ${result.total} users` : result.message}
          showIcon
          style={{ marginBottom: 20 }}
          closable
          onClose={() => setResult(null)}
        />
      )}

      <Row gutter={24}>
        <Col xs={24} lg={14}>
          <Card title="Notification Content" style={{ borderRadius: 10, marginBottom: 16 }}>
            <Form form={form} layout="vertical" onFinish={onFinish}>

              <Form.Item
                name="title"
                label="Title"
                rules={[
                  { required: true, message: 'Title is required' },
                  { max: 250, message: 'Max 250 characters' },
                ]}
                extra={<Text type="secondary" style={{ fontSize: 12 }}>Use <code>{'{name}'}</code> to insert the user's name</Text>}
              >
                <Input placeholder="Hello {name}!" maxLength={250} showCount />
              </Form.Item>

              <Form.Item
                name="message"
                label="Message"
                rules={[{ required: true, message: 'Message is required' }]}
              >
                <TextArea rows={4} placeholder="Notification body text…" />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="language"
                    label="Language"
                    rules={[{ required: true, message: 'Language is required' }]}
                  >
                    <Select placeholder="Select language" options={LANGUAGES} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="type"
                    label="Type"
                    rules={[{ required: true, message: 'Type is required' }]}
                  >
                    <Select placeholder="Select type" options={TYPES} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left" style={{ fontSize: 13, color: '#888' }}>Optional</Divider>

              <Form.Item name="link" label="Link" rules={[{ max: 200, message: 'Max 200 characters' }]}>
                <Input placeholder="https://… or deep-link" maxLength={200} />
              </Form.Item>

              <Form.Item name="link_title" label="Link Button Label">
                <Input placeholder="e.g. See what's new" />
              </Form.Item>

              <Form.Item label="In-app image" extra="Shown in the app's notification list · JPEG / PNG / WebP · max 5 MB">
                <Upload
                  beforeUpload={beforeUpload}
                  maxCount={1}
                  onRemove={() => setImageFile(null)}
                  fileList={imageFile ? [{ uid: '-1', name: imageFile.name, status: 'done' }] : []}
                >
                  <Button icon={<UploadOutlined />}>Choose Image</Button>
                </Upload>
              </Form.Item>

              <Form.Item label="Push notification image" extra="Shown on the phone's notification (Android) · JPEG / PNG · max 1 MB · wide, 2:1 (e.g. 1024×512)">
                <Upload
                  beforeUpload={beforePushUpload}
                  maxCount={1}
                  onRemove={() => setPushImageFile(null)}
                  fileList={pushImageFile ? [{ uid: '-2', name: pushImageFile.name, status: 'done' }] : []}
                >
                  <Button icon={<UploadOutlined />}>Choose Push Image</Button>
                </Upload>
              </Form.Item>

              <Divider orientation="left" style={{ fontSize: 13, color: '#888' }}>Audience Filters</Divider>

              <Space direction="vertical" size={16} style={{ width: '100%', marginBottom: 16 }}>
                <Form.Item name="not_subscribed" valuePropName="checked" noStyle>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                    <div>
                      <Text strong style={{ fontSize: 13 }}>Non-subscribers only</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>Users who have never subscribed</Text>
                    </div>
                    <Form.Item name="not_subscribed" valuePropName="checked" noStyle>
                      <Switch />
                    </Form.Item>
                  </div>
                </Form.Item>

                <div style={{ padding: '10px 14px', background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <Text strong style={{ fontSize: 13 }}>Expired subscribers only</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>Users whose latest subscription has expired</Text>
                    </div>
                    <Form.Item name="expired_users" valuePropName="checked" noStyle>
                      <Switch />
                    </Form.Item>
                  </div>
                </div>

                <div style={{ padding: '10px 14px', background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: outdatedUsers ? 12 : 0 }}>
                    <div>
                      <Text strong style={{ fontSize: 13 }}>Outdated app version only</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>Users not on the latest app version</Text>
                    </div>
                    <Form.Item name="outdated_users" valuePropName="checked" noStyle>
                      <Switch
                        onChange={(val) => {
                          setOutdatedUsers(val);
                          if (!val) form.setFieldValue('latest_app_version', undefined);
                        }}
                      />
                    </Form.Item>
                  </div>
                  {outdatedUsers && (
                    <Form.Item
                      name="latest_app_version"
                      rules={[{ required: true, message: 'App version is required when filtering outdated users' }]}
                      style={{ marginBottom: 0, marginTop: 8 }}
                    >
                      <Input placeholder="e.g. 1.4.95" style={{ maxWidth: 200 }} />
                    </Form.Item>
                  )}
                </div>
              </Space>

              <Form.Item style={{ marginTop: 8, marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  icon={<SendOutlined />}
                  size="large"
                  style={{ minWidth: 160 }}
                >
                  Send Notification
                </Button>
              </Form.Item>

            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card
            title={<><InfoCircleOutlined style={{ marginRight: 8 }} />Tips</>}
            style={{ borderRadius: 10 }}
          >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <div>
                <Text strong style={{ fontSize: 13 }}>Personalisation</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Use <code>{'{name}'}</code> in title or message — the server replaces it with each user's first name (falls back to "User").
                </Text>
              </div>
              <Divider style={{ margin: '4px 0' }} />
              <div>
                <Text strong style={{ fontSize: 13 }}>Supported languages</Text>
                <div style={{ marginTop: 6 }}>
                  {LANGUAGES.map((l) => <Tag key={l.value} style={{ marginBottom: 4 }}>{l.value}</Tag>)}
                </div>
              </div>
              <Divider style={{ margin: '4px 0' }} />
              <div>
                <Text strong style={{ fontSize: 13 }}>Notification types</Text>
                <div style={{ marginTop: 6 }}>
                  {TYPES.map((t) => <Tag key={t.value} color="green" style={{ marginBottom: 4 }}>{t.value}</Tag>)}
                </div>
              </div>
              <Divider style={{ margin: '4px 0' }} />
              <div>
                <Text strong style={{ fontSize: 13 }}>Images</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <b>In-app image</b> shows in the app's notification list (JPEG, PNG or WebP · max 5 MB).
                  <br />
                  <b>Push image</b> shows on the phone's notification — Android only (JPEG or PNG · max 1 MB · wide 2:1).
                  <br />
                  Both are optional and independent — leave empty to send text-only.
                </Text>
              </div>
              <Divider style={{ margin: '4px 0' }} />
              <div>
                <Text strong style={{ fontSize: 13 }}>Filters are additive</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Enabling multiple filters sends to the union of matching users.
                </Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
